import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Tooltip, 
  IconButton, 
  TextField,
  Snackbar
} from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useRoleAccess } from '../hooks/useRoleAccess';

export default function ProjectReporting() {
  const navigate = useNavigate();
  const { canAccessAdminArea } = useRoleAccess();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch projects data
  const fetchProjects = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(API_ENDPOINTS.PROJECTS);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const projects = data.projects || data || [];
      
      // Sort by creation date (newest first)
      const sortedProjects = projects.sort((a, b) => {
        const dateA = new Date(a.created_at || a.createdAt || 0);
        const dateB = new Date(b.created_at || b.createdAt || 0);
        return dateB - dateA;
      });
      
      setRows(sortedProjects);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle inline editing start
  const handleEditStart = (id, currentValue) => {
    setEditingId(id);
    setEditingValue(currentValue || '');
  };

  // Handle inline editing save
  const handleEditSave = async (id) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.PROJECTS}?id=${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          external_reference: editingValue
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Update local state
      setRows(prevRows => 
        prevRows.map(row => 
          row.id === id 
            ? { ...row, external_reference: editingValue }
            : row
        )
      );

      setSnackbar({
        open: true,
        message: 'External reference updated successfully!',
        severity: 'success'
      });

      setEditingId(null);
      setEditingValue('');
    } catch (err) {
      console.error('Error updating external reference:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update external reference. Please try again.',
        severity: 'error'
      });
    }
  };

  // Handle inline editing cancel
  const handleEditCancel = () => {
    setEditingId(null);
    setEditingValue('');
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Check if user has admin access
  if (!canAccessAdminArea()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1">
            This page is only accessible to administrators.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Define columns for DataGrid
  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      hide: true
    },
    {
      field: 'customer_name',
      headerName: 'Customer',
      width: 150,
      sortable: true,
      valueGetter: (params) => {
        return params.row.customer?.name || 'N/A';
      }
    },
    {
      field: 'project_code',
      headerName: 'Code',
      width: 120,
      sortable: true
    },
    {
      field: 'name',
      headerName: 'Project Name',
      width: 200,
      sortable: true
    },
    {
      field: 'external_reference',
      headerName: 'External Reference',
      width: 200,
      sortable: true,
      renderCell: (params) => {
        const isEditing = editingId === params.row.id;
        
        if (isEditing) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                sx={{ width: 150 }}
                autoFocus
              />
                             <IconButton 
                 size="small" 
                 onClick={() => handleEditSave(params.row.id)}
                 sx={{ color: 'green' }}
               >
                 <SaveIcon />
               </IconButton>
              <IconButton 
                size="small" 
                onClick={handleEditCancel}
                color="error"
              >
                <CancelIcon />
              </IconButton>
            </Box>
          );
        }
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {params.value || '-'}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => handleEditStart(params.row.id, params.value)}
            >
              <EditIcon />
            </IconButton>
          </Box>
        );
             }
     },
     {
       field: 'description',
       headerName: 'Description',
       width: 250,
       sortable: true
     },
     {
       field: 'active',
       headerName: 'Status',
       width: 100,
       sortable: true,
      renderCell: (params) => (
        <Box 
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: params.value ? '#4caf50' : '#bdbdbd',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '0 auto'
          }}
          title={params.value ? 'Active' : 'Inactive'}
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 150,
      sortable: true,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    }
  ];

  return (
    <Box>
      <Box my={4}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem', mb: 3 }}>
          Manage Project External References
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {error}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This is likely a backend API issue. Please check the browser console for more details.
            </Typography>
            <Tooltip title="Retry loading projects">
              <IconButton 
                size="small" 
                onClick={fetchProjects}
                sx={{ mr: 1 }}
              >
                🔄 Retry
              </IconButton>
            </Tooltip>
          </Alert>
        ) : (
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              pagination
              sx={{ bgcolor: 'white' }}
              disableSelectionOnClick
              getRowId={(row) => row.id}
            />
          </Box>
        )}
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
